<template>
  <v-container fluid>
    <PageHeader
      title="Sightings"
      icon="mdi-calendar-check"
    >
      <template #actions>
        <v-row>
          <v-col class="text-right">
            <template v-if="hasScope('sightings:write')">
              <v-btn
                color="primary"
                class="mr-2"
                @click="createSighting"
              >
                Create Sighting
              </v-btn>
              <v-btn
                color="primary"
                class="mr-2"
                @click="exportData"
              >
                Export as CSV
              </v-btn>
              <v-btn
                color="primary"
                class="mr-2"
                @click="openImportDialog"
              >
                Import CSV
              </v-btn>
              <v-btn
                color="primary"
                @click="openImageUploadDialog"
              >
                Import Images
              </v-btn>
            </template>
          </v-col>
        </v-row>
      </template>
    </PageHeader>
    <v-row>
      <v-col cols="12">
        <v-row>
          <v-col class="pr-6">
            <v-menu
              v-model="dateField"
              :close-on-content-click="false"
              :offset="40"
              transition="scale-transition"
              min-width="auto"
            >
              <template #activator="{ props }">
                <v-text-field
                  v-model="dateRangeText"
                  label="Date Filter"
                  prepend-icon="mdi-calendar"
                  readonly
                  density="compact"
                  v-bind="props"
                  clearable
                  v-on="props"
                  @click:clear="dateTimeFullText = null"
                />
              </template>
              <v-date-picker
                v-model="dateTimeFullText"
                multiple="range"
                show-adjacent-months
              />
            </v-menu>
          </v-col>
          <v-col>
            <v-text-field
              v-model="query"
              prepend-icon="mdi-magnify"
              label="Search"
              density="compact"
              single-line
              hide-details
              clearable
            />
          </v-col>
          <v-col>
            <v-select
              v-model="selectedSpecies"
              :items="Object.values(enums.sightingSpecies)"
              label="Species"
              name="species"
              density="compact"
              multiple
              clearable
              @keyup="executeSearch"
            >
              <template #selection="{ item, index }">
                <span v-if="index < maxSelectDisplay">{{ item }} &nbsp;</span>
                <span
                  v-if="index === maxSelectDisplay"
                  class="grey-text text-caption"
                >(+{{ selectedSpecies.length - maxSelectDisplay }} more)</span>
              </template>
              <template #prepend>
                <humpback-icon />
              </template>
            </v-select>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-sheet
          elevation="2"
          class="pa-md-2"
        >
          <v-tabs
            v-model="archivedTab"
            color="primary"
            class="mb-2"
          >
            <v-tab :value="false">
              Active
            </v-tab>
            <v-tab :value="true">
              Archived
            </v-tab>
          </v-tabs>
          <v-data-table-server
            :headers="tableHeaders"
            :items="items"
            :loading="loading"
            :v-model:options="options"
            :items-length="total"
            :row-props="rowProps"
            @click:row="handleRowClick"
          >
            <template #[`item.actions`]="{ item }">
              <v-tooltip location="bottom">
                <template #activator="{ props }">
                  <v-icon
                    size="small"
                    v-bind="props"
                    v-on="props"
                    @click="openDrawer(item)"
                  >
                    mdi-arrow-expand
                  </v-icon>
                </template>
                <span>View Sighting</span>
              </v-tooltip>
            </template>
          </v-data-table-server>
        </v-sheet>
      </v-col>
    </v-row>
    <v-navigation-drawer
      v-model="drawerOpen"
      location="right"
      width="450"
    >
      <DrawerCard>
        <v-row
          v-if="selectedSighting && selectedSighting.id"
          class="pt-4"
        >
          <v-col>
            <v-row>
              <v-col cols="12">
                <photo-carousel :images="sightingImages" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model="selectedSighting.sightingNumber"
                  label="Sighting Number*"
                  readonly
                  variant="outlined"
                  density="compact"
                  hide-details
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="selectedSighting.sightingUuid"
                  label="Sighting Uuid"
                  readonly
                  variant="outlined"
                  density="compact"
                  hide-details
                />
              </v-col>
            </v-row>
            <div
              class="font-weight-bold pt-4 text-h6"
            >
              Sighting Info
            </div>
            <v-divider class="divider" />
            <v-row>
              <v-col cols="3">
                <v-text-field
                  v-model="selectedSighting.sightingYear"
                  label="Year*"
                  readonly
                  variant="outlined"
                  density="compact"
                  hide-details
                />
              </v-col>
              <v-col cols="3">
                <v-text-field
                  v-model="selectedSighting.sightingMonth"
                  label="Month"
                  readonly
                  variant="outlined"
                  density="compact"
                  hide-details
                />
              </v-col>
              <v-col cols="3">
                <v-text-field
                  v-model="selectedSighting.sightingDay"
                  label="Day*"
                  readonly
                  variant="outlined"
                  density="compact"
                  hide-details
                />
              </v-col>
              <v-col cols="3">
                <v-text-field
                  v-model="selectedSighting.sightingTime"
                  label="Time"
                  readonly
                  variant="outlined"
                  density="compact"
                  hide-details
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model="selectedSighting.lat"
                  label="Latitude"
                  type="number"
                  hide-spin-buttons
                  readonly
                  variant="outlined"
                  density="compact"
                  hide-details
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="selectedSighting.lon"
                  label="Longitude"
                  type="number"
                  hide-spin-buttons
                  readonly
                  variant="outlined"
                  density="compact"
                  hide-details
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="selectedSighting.weather"
                  label="Weather"
                  variant="outlined"
                  readonly
                  density="compact"
                  hide-details
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <template #actions>
          <v-card-actions v-if="hasScope('sightings:write')">
            <v-btn
              color="primary"
              size="small"
              :disabled="sightingDialog"
              @click="editItem(selectedSighting)"
            >
              <v-icon>mdi-pencil</v-icon>
              Edit
            </v-btn>
            <v-spacer />
            <v-btn
              v-if="!archived"
              color="warning"
              size="small"
              @click="openArchive(selectedSighting)"
            >
              <v-icon>mdi-archive-arrow-down</v-icon>
              Archive
            </v-btn>
            <v-btn
              v-else
              color="success"
              size="small"
              @click="openRestore(selectedSighting)"
            >
              <v-icon>mdi-archive-arrow-up</v-icon>
              Restore
            </v-btn>
            <v-btn
              color="error"
              size="small"
              :disabled="deleteDialog"
              @click="deleteItem(selectedSighting)"
            >
              <v-icon>mdi-delete</v-icon>
              Delete
            </v-btn>
          </v-card-actions>
          <v-card-actions v-else>
            <v-spacer />
            <v-btn
              color="primary"
              :disabled="sightingDialog"
              @click="editItem(selectedSighting)"
            >
              <v-icon>mdi-eye</v-icon>
              View Details
            </v-btn>
            <v-spacer />
          </v-card-actions>
        </template>
      </DrawerCard>
    </v-navigation-drawer>
    <sighting-details />
    <import-data />
    <export-filter />
    <photo-upload />
    <delete
      :id="selectedItem.id"
      :name="selectedItem.sightingNumber"
      :delete-action="handleDelete"
    />
    <archive
      :id="selectedItem.sightingNumber"
      :name="selectedItem.sightingNumber"
      :action="setSightingArchived"
      :dialog="archiveDialog"
      :close="closeArchive"
      @update:dialog="archiveDialog = $event"
    />
    <restore
      :id="selectedItem.sightingNumber"
      :name="selectedItem.sightingNumber"
      :action="setSightingArchived"
      :dialog="restoreDialog"
      :close="closeRestore"
      @update:dialog="restoreDialog = $event"
    />
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DrawerCard from "@/components/DrawerCard";
import PageHeader from "@/components/PageHeader";
import { debounce } from "lodash";
import SightingDetails from "@/components/sighting/Details.vue";
import enums from "@/helpers/enums";
import HumpbackIcon from "@/components/svgs/HumpbackIcon.vue";
import ImportData from "../../components/sighting/ImportData.vue";
import ExportFilter from "../../components/sighting/ExportFilter.vue";
import PhotoUpload from "../../components/PhotoUpload.vue";
import Delete from "@/components/Delete.vue";
import Archive from "@/components/Archive.vue";
import Restore from "@/components/Restore.vue";
import PhotoCarousel from "@/components/PhotoCarousel.vue";
import sightingsApi from "@/services/api/sightings";

export default {
  components: {
    DrawerCard,
    PageHeader,
    SightingDetails,
    HumpbackIcon,
    ImportData,
    ExportFilter,
    PhotoUpload,
    Delete,
    Archive,
    Restore,
    PhotoCarousel,
  },
  data() {
    return {
      maxSelectDisplay: 1,
      visible: true,
      query: "",
      dateTimeFullText: null,
      dateField: false,
      selectedIncidentNatures: [],
      selectedSpecies: [],
      options: {},
      selectedSighting: {},
      selectedItem: {},
      drawerOpen: false,
      archiveDialog: false,
      restoreDialog: false,
      sightingImages: [],
      fullHeaders: [
        { title: "Sighting Number", sortable: true, key: "sightingNumber" },
        { title: "Id", sortable: true, key: "whaleId" },
        { title: "Name", sortable: true, key: "whaleName" },
        {
          title: "Sighting Date",
          sortable: true,
          key: "sightingDate",
        },
        { title: "Sighting Time", sortable: true, key: "sightingTime" },
        { title: "Species Involved", sortable: true, key: "species" },
        {
          title: "Actions",
          sortable: false,
          key: "actions",
          align: "center",
          width: "10%",
        },
      ],
      compactHeaders: [
        { title: "Sighting Number", sortable: true, key: "sightingNumber" },
        { title: "Species Involved", sortable: true, key: "species" },
        {
          title: "Actions",
          sortable: false,
          key: "actions",
          align: "center",
          width: "10%",
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      loading: "sightings/loading",
      items: "sightings/items",
      searchOptions: "sightings/searchOptions",
      searchQuery: "sightings/searchQuery",
      searchDate: "sightings/searchDate",
      total: "sightings/total",
      sightingDialog: "sighting/details/dialog",
      sightingUploadDialog: "sighting/import/dialog",
      imageUploadDialog: "images/dialog",
      deleteDialog: "delete/dialog",
      hasScope: "auth/hasScope",
      archived: "sightings/archived",
    }),
    startDatetime () {
      if(this.dateTimeFullText) {
        if(this.dateTimeFullText.length > 1) {
          return this.dateTimeFullText[0].toString();
        }
        else {
          return this.dateTimeFullText?.toString();
        }
      }
      else {
        return "";
      }
    },
    startYear () {
      return this.startDatetime?.split(" ")[3];
    },
    startMonth () {
     const numericalMonth = this.enums.shortMonths.indexOf(this.startDatetime?.split(" ")[1]) + 1;
     if (numericalMonth < 10) {
       return `0${numericalMonth}`;
     }
     else {
       return numericalMonth;
     }
    },
    startDay () {
      return this.startDatetime?.split(" ")[2];
    },
    startDate () {
      return `${this.startYear}-${this.startMonth}-${this.startDay}`;
    },
    startTime () {
      return this.startDatetime?.split(" ")[4]
    },
    endDatetime () {
      if(this.dateTimeFullText) {
        if(this.dateTimeFullText.length > 1) {
          return this.dateTimeFullText[this.dateTimeFullText.length-1].toString();
        }
        else {
          return "";
        }
      }
      else {
        return "";
      }
    },
    endYear () {
      return this.endDatetime?.split(" ")[3];
    },
    endMonth () {
      const numericalMonth = this.enums.shortMonths.indexOf(this.endDatetime?.split(" ")[1]) + 1;
      if (numericalMonth < 10) {
        return `0${numericalMonth}`;
      }
      else {
        return numericalMonth;
      }
    },
    endDay () {
      return this.endDatetime?.split(" ")[2];
    },
    endDate () {
      return `${this.endYear}-${this.endMonth}-${this.endDay}`;
    },
    endTime () {
      return this.endDatetime?.split(" ")[4]
    },
    dateRangeText () {
      if (this.startYear !== "" && this.startYear !== undefined) {
        if (this.endYear !== "" && this.endYear !== undefined) {
          return `${this.startDate}~${this.endDate}`;
        }
        else {
          return this.startDate;
        }
      }
      else {
        console.log(this.dateTimeFullText)
        return "";
      }
    },
    enums() {
      return enums;
    },
    tableHeaders() {
      return this.drawerOpen ? this.compactHeaders : this.fullHeaders;
    },
    archivedTab: {
      get() {
        return this.archived;
      },
      set(value) {
        this.setArchived(value);
        this.drawerOpen = false;
        this.executeSearch();
      },
    },
  },
  watch: {
    query() {
      if (this.query == null) this.query = "";
      this.debouncedSearch();
      this.options.page = 1;
    },
    dateTimeFullText() {
      this.debouncedSearch();
      this.options.page = 1;
    },
    async selectedIncidentNatures() {
      await this.executeSearch()
    },
    async selectedSpecies() {
      await this.executeSearch()
    },
    options() {
      this.executeSearch();
    },
  },
  async mounted() {
    await this.reload();
    await this.consumePendingEdit();
  },
  created() {
    this.options = this.searchOptions;
    this.query = this.searchQuery;
    if (this.searchDate != null) this.dateRangeText = this.searchDate.join("~");
    if (this.searchIncidentNature != null) {
      this.selectedIncidentNatures = this.searchIncidentNature;
    }
    if (this.searchSpecies != null) {
      this.selectedSpecies = this.searchSpecies;
    }
    this.debouncedSearch = debounce(this.executeSearch, 250);
    this.close()
  },
  methods: {
    resolveStorageUrl(url) {
      if (!url) return "";
      if (url.startsWith("http")) return url;
      return `${process.env.VUE_APP_API_URL}${url}`;
    },
    ...mapActions({
      search: "sightings/search",
      open: "sighting/details/open",
      reload: "sightings/reload",
      close: "sighting/details/close",
      openExport: "sighting/export/open",
      openImport: "sighting/import/open",
      openImageImport: "images/open",
      openDelete: "delete/open",
      deleteSighting: "sightings/deleteSighting",
      consumePendingEdit: "sighting/details/consumePendingEdit",
      setArchived: "sightings/setArchived",
      setSightingArchived: "sightings/setSightingArchived",
    }),
    async executeSearch() {
      await this.search({
        ...this.options,
        query: this.query,
        date: this.dateRangeText?.split("~"),
        incidentNature: this.selectedIncidentNatures,
        species: this.selectedSpecies,
      });
    },
    async openDrawer(item) {
      this.selectedSighting = { ...item };
      this.drawerOpen = true;
      this.sightingImages = [];
      if (item.sightingNumber) {
        const result = await sightingsApi.imagesForSighting(item.sightingNumber);
        this.sightingImages = result.data;
      }
    },
    rowProps(data) {
      const item = data.item;
      if (item && this.selectedSighting && item.id === this.selectedSighting.id) {
        return { class: "selected-row" };
      }
      return {};
    },
    handleRowClick(e, rowObject) {
      this.openDrawer(rowObject.item);
    },
    editItem(item) {
      this.open({ sighting: item });
    },
    deleteItem(item) {
      this.selectedItem = item;
      this.openDelete();
    },
    openArchive(item) {
      this.selectedItem = item;
      this.archiveDialog = true;
    },
    openRestore(item) {
      this.selectedItem = item;
      this.restoreDialog = true;
    },
    closeArchive() {
      this.archiveDialog = false;
      this.drawerOpen = false;
      this.executeSearch();
    },
    closeRestore() {
      this.restoreDialog = false;
      this.drawerOpen = false;
      this.executeSearch();
    },
    async handleDelete() {
      await this.deleteSighting(this.selectedItem.sightingNumber);
      this.drawerOpen = false;
    },
    async createSighting() {
      await this.open({ sighting: null });
    },
    async exportData() {
      await this.openExport()
    },
    async openImportDialog() {
      await this.openImport()
    },
    async openImageUploadDialog() {
      await this.openImageImport()
    },
  },
};
</script>

<style>




.center-vertically {
  align-items: center;
  display: flex;
}

:deep(.v-list__tile) {
  height: 1rem;
}

.selected-row {
  background-color: #79d9f4;
}

</style>
